import { useState, useRef, useCallback, useEffect } from 'react';

export interface UseStepHeaderWithTargetElementsParams {
  targetKeys: string[];
  rootId?: string;
  // 接した際にアクティブになる境界線の上部からのオフセット
  activeBorderOffset?: number;
  scrollTopOffset?: number;
  onActiveTargetChange?: (targetKey: string) => void;
}

export interface UseStepHeaderWithTargetElementsValue {
  scrollToTarget: (targetKey: string) => void;
}

export const useStepHeaderWithTargetElements: (
  params: UseStepHeaderWithTargetElementsParams,
) => UseStepHeaderWithTargetElementsValue = ({
  targetKeys,
  onActiveTargetChange,
  rootId,
  activeBorderOffset = 0,
  scrollTopOffset = 0,
}) => {
  const [activeTargetKey, setActiveTargetKey] = useState<string | undefined>(
    undefined,
  );
  const scrollTargetTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);

  const scrollToTarget = useCallback(
    (targetKey: string) => {
      const rootElement =
        typeof rootId !== 'undefined' ? document.getElementById(rootId) : null;

      const targetElements = (() => {
        if (rootElement === null) {
          return Array.from(
            document.querySelectorAll('[data-step-header-target]'),
          ).filter((element) => {
            return targetKeys.includes(
              element.getAttribute('data-step-header-target') || '',
            );
          });
        }

        return Array.from(
          rootElement.querySelectorAll('[data-step-header-target]'),
        ).filter((element) => {
          return targetKeys.includes(
            element.getAttribute('data-step-header-target') || '',
          );
        });
      })();

      if (targetElements.length === 0) return;

      const scrolledTargetElement = targetElements.find((element) => {
        return element.getAttribute('data-step-header-target') === targetKey;
      }) as HTMLElement | undefined;

      if (!scrolledTargetElement) return;

      const rootRectTop =
        rootElement !== null ? rootElement.getBoundingClientRect().top : 0;
      const rectTop = scrolledTargetElement.getBoundingClientRect().top;
      const scrollTop =
        rootElement !== null ? rootElement.scrollTop : window.scrollY;

      const top = rectTop - rootRectTop + scrollTop - scrollTopOffset;

      // スクロール中であることを判定するフラグ
      scrolledTargetElement.dataset.stepHeaderScrollTarget = 'true';

      setActiveTargetKey(targetKey);

      if (rootElement === null) {
        window.scrollTo({
          top,
          behavior: 'smooth',
        });
        return;
      }

      rootElement.scrollTo({
        top,
        behavior: 'smooth',
      });
    },
    [targetKeys, rootId, scrollTopOffset],
  );

  useEffect(() => {
    if (typeof activeTargetKey === 'undefined') return;
    onActiveTargetChange?.(activeTargetKey);
  }, [onActiveTargetChange, activeTargetKey]);

  // IntersectionObserverを用いたターゲットのviewport内での表示領域の変化の監視
  useEffect(() => {
    const rootElement =
      typeof rootId !== 'undefined' ? document.getElementById(rootId) : null;

    const targetElements = (() => {
      if (rootElement === null) {
        return Array.from(
          document.querySelectorAll('[data-step-header-target]'),
        ).filter((element) => {
          return targetKeys.includes(
            element.getAttribute('data-step-header-target') || '',
          );
        });
      }

      return Array.from(
        rootElement.querySelectorAll('[data-step-header-target]'),
      ).filter((element) => {
        return targetKeys.includes(
          element.getAttribute('data-step-header-target') || '',
        );
      });
    })() as HTMLElement[];

    if (targetElements.length === 0) return;

    const observer = new IntersectionObserver(
      () => {
        // scrollToTargetによるスクロール中は評価を行わない
        const scrolledTargetElement = targetElements.find(
          (element) => element.dataset.stepHeaderScrollTarget === 'true',
        );
        if (scrolledTargetElement) {
          const activeTargetKey = scrolledTargetElement.getAttribute(
            'data-step-header-target',
          );

          if (activeTargetKey) {
            if (typeof scrollTargetTimeoutRef.current !== 'undefined') {
              clearTimeout(scrollTargetTimeoutRef.current);
            }

            scrollTargetTimeoutRef.current = setTimeout(() => {
              // スクロール中のフラグを削除
              delete scrolledTargetElement.dataset.stepHeaderScrollTarget;
              scrollTargetTimeoutRef.current = undefined;
            }, 100);
            return;
          }
        }

        // フォーカスが当たっている要素を含むターゲットがある場合
        const focusedTargetElement = targetElements.find((element) =>
          element.contains(document.activeElement),
        );
        if (focusedTargetElement && document.activeElement !== null) {
          // フォーカスが当たっている要素がactiveBorderOffsetを考慮したviewport内にある場合はそのターゲットをアクティブにする
          const activeElement = document.activeElement;
          const activeElementRect = activeElement.getBoundingClientRect();

          if (
            activeElementRect.top >= activeBorderOffset &&
            activeElementRect.bottom <= window.innerHeight
          ) {
            const activeTargetKey = focusedTargetElement.getAttribute(
              'data-step-header-target',
            );

            if (activeTargetKey) {
              setActiveTargetKey(activeTargetKey);
              return;
            }
          }
        }

        // topBorderOffsetを超えているtargetElementの取得
        let activeTargetElement: Element = targetElements[0];
        targetElements.forEach((element) => {
          const rootRectTop =
            rootElement !== null ? rootElement.getBoundingClientRect().top : 0;
          const rectTop = element.getBoundingClientRect().top;

          if (rectTop - rootRectTop <= activeBorderOffset) {
            activeTargetElement = element;
          }
        });

        const activeTargetKey = activeTargetElement.getAttribute(
          'data-step-header-target',
        );

        if (activeTargetKey) {
          setActiveTargetKey(activeTargetKey);
          return;
        }
      },
      {
        root: rootElement,
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      },
    );

    targetElements.forEach((targetElement) => {
      observer.observe(targetElement);
    });

    return () => {
      observer.disconnect();
    };
  }, [targetKeys, rootId, activeBorderOffset]);

  // ターゲットに含まれる要素のフォーカスの監視
  useEffect(() => {
    const rootElement =
      typeof rootId !== 'undefined' ? document.getElementById(rootId) : null;

    const targetElements = (() => {
      if (rootElement === null) {
        return Array.from(
          document.querySelectorAll('[data-step-header-target]'),
        ).filter((element) => {
          return targetKeys.includes(
            element.getAttribute('data-step-header-target') || '',
          );
        });
      }

      return Array.from(
        rootElement.querySelectorAll('[data-step-header-target]'),
      ).filter((element) => {
        return targetKeys.includes(
          element.getAttribute('data-step-header-target') || '',
        );
      });
    })();

    if (targetElements.length === 0) return;

    const onFocus = (event: Event) => {
      const targetElement = event.currentTarget as Element;

      const activeTargetKey = targetElement.getAttribute(
        'data-step-header-target',
      );

      if (activeTargetKey) {
        setActiveTargetKey(activeTargetKey);
        return;
      }
    };

    targetElements.forEach((element) => {
      element.addEventListener('focus', onFocus, true);
    });

    return () => {
      targetElements.forEach((element) => {
        element.removeEventListener('focus', onFocus, true);
      });
    };
  }, [targetKeys, rootId]);

  return { scrollToTarget };
};
